import React from 'react';
import { AlertTriangle, LogOut, Clock } from 'lucide-react';

/**
 * IdleWarningDialog — countdown modal shown when the session is about to
 * auto-expire from inactivity. Renders only when `open` is true.
 *
 * The user has two choices:
 *   - "Stay signed in" → resets the idle timers (calls `onStay`)
 *   - "Sign out now"    → immediate logout (calls `onLogoutNow`)
 *
 * If neither button is clicked before the countdown hits 0, the parent hook
 * will call its own onLogout handler.
 */
const IdleWarningDialog = ({ open, secondsRemaining, onStay, onLogoutNow }) => {
  if (!open) return null;

  const mm = String(Math.floor(secondsRemaining / 60)).padStart(2, '0');
  const ss = String(secondsRemaining % 60).padStart(2, '0');

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm"
      data-testid="idle-warning-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="idle-warning-title"
    >
      <div
        className="w-full max-w-md mx-4 bg-white rounded-lg shadow-2xl overflow-hidden border border-slate-200"
        data-testid="idle-warning-dialog"
      >
        <div className="bg-gradient-to-br from-amber-500 to-orange-500 px-5 py-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
            <AlertTriangle size={18} className="text-white" />
          </div>
          <div>
            <h2 id="idle-warning-title" className="text-white font-semibold text-sm">
              Session about to expire
            </h2>
            <p className="text-white/85 text-xs mt-0.5">
              You&apos;ve been inactive. For security, we&apos;ll sign you out shortly.
            </p>
          </div>
        </div>

        <div className="px-5 py-5">
          <div className="flex items-center justify-center gap-2 py-3">
            <Clock size={20} className="text-slate-500" />
            <span
              className="font-mono text-3xl font-bold text-slate-800 tabular-nums"
              data-testid="idle-warning-countdown"
              aria-live="polite"
            >
              {mm}:{ss}
            </span>
          </div>
          <p className="text-xs text-center text-slate-500">
            Any activity or clicking the button below will keep you signed in.
          </p>
        </div>

        <div className="px-5 py-4 bg-slate-50 border-t border-slate-200 flex gap-2 justify-end">
          <button
            type="button"
            onClick={onLogoutNow}
            className="px-3 py-2 text-xs font-medium text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-sm inline-flex items-center gap-1.5 transition-colors"
            data-testid="idle-warning-logout-btn"
          >
            <LogOut size={13} />
            Sign out now
          </button>
          <button
            type="button"
            onClick={onStay}
            autoFocus
            className="px-4 py-2 text-xs font-semibold text-white bg-[#0F172A] hover:bg-[#1e293b] rounded-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
            data-testid="idle-warning-stay-btn"
          >
            Stay signed in
          </button>
        </div>
      </div>
    </div>
  );
};

export default IdleWarningDialog;
